import { useState, useEffect, useCallback } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { generateDeviceFingerprint, getDeviceName } from "@/lib/deviceFingerprint";
import api from "@/lib/api";

import { getApiUrl } from "@/lib/apiUrl";
const API_BASE_URL = `${getApiUrl()}/api/v1`;
const PROMPT_KEY = "prezntz_show_trust_prompt";
const TRUSTED_KEY = "prezntz_trusted_device";

/**
 * Floating prompt shown after Google One Tap sign-in asking the user
 * if they want to trust this device for 90-day sessions.
 *
 * Triggered by sessionStorage flag set in useGoogleOneTap callback.
 * When accepted:
 * 1. Generates a device fingerprint and registers it server-side
 * 2. Rotates the refresh token with rememberMe=true for a 30-day token
 */
export function TrustDevicePrompt() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if One Tap just signed in and device isn't already trusted
    const shouldShow = sessionStorage.getItem(PROMPT_KEY) === "true";
    const alreadyTrusted = localStorage.getItem(TRUSTED_KEY) === "true";

    if (shouldShow && !alreadyTrusted) {
      // Small delay so the main navigation completes first
      const timer = setTimeout(() => setVisible(true), 1500);
      sessionStorage.removeItem(PROMPT_KEY);
      return () => clearTimeout(timer);
    }

    if (shouldShow) {
      sessionStorage.removeItem(PROMPT_KEY);
    }
  }, []);

  // Auto-dismiss after 15 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => setVisible(false), 15000);
    return () => clearTimeout(timer);
  }, [visible]);

  const handleTrust = useCallback(async () => {
    setLoading(true);
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        setVisible(false);
        return;
      }

      // Generate device fingerprint and register server-side
      const fingerprint = await generateDeviceFingerprint();
      const deviceName = getDeviceName();

      // Register trusted device with the backend (non-blocking if it fails)
      api.post("/users/me/devices/trust", { fingerprint, deviceName }).catch(() => {});

      // Rotate to a 30-day refresh token
      const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
        refreshToken,
        rememberMe: true,
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("refresh_token", newRefreshToken);
      localStorage.setItem(TRUSTED_KEY, "true");
    } catch {
      // Non-critical — session still works with the existing token
    } finally {
      setLoading(false);
      setVisible(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="fixed bottom-6 right-6 z-50 max-w-sm"
        >
          <div className="glass-card border border-white/10 rounded-xl p-4 shadow-2xl shadow-black/50">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gift-gradient flex items-center justify-center">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    Trust this device?
                  </h4>
                  <button
                    onClick={handleDismiss}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Stay signed in for 90 days on this device.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="gradient"
                    onClick={handleTrust}
                    disabled={loading}
                    className="text-xs h-7 px-3"
                  >
                    {loading ? "Saving..." : "Trust device"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDismiss}
                    className="text-xs h-7 px-3"
                  >
                    Not now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
